import React, { useState, useMemo, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import clsx from 'clsx';
import Modal from '../common/Modal';
import styles from './MoreGamesPopup.module.css';
import { getGamesByTitles, type GameThumbnail } from '../../services/gamePerformanceService';
import { useViewModel } from '../../hooks/useViewModel';
import { usePopularGames } from '../../hooks/usePopularGames';
import { useAllGameTitles } from "../../hooks/useAllGameTitles";
import { Check } from "../common/icons";

const MAX_SELECTION = 12;

const norm = (title: string) => title.toLowerCase().trim();

const KEY_ENTER = 'Enter';
const KEY_SPACE = ' ';
const S3_ORIGIN = 'https://s3.amazonaws.com';

const THUMBNAIL_WIDTH = 184;
const THUMBNAIL_HEIGHT = 69;

interface MoreGamesPopupV2Props {
	isOpen: boolean;
	isPrerendering?: boolean;
	onClose: () => void;
	onConfirm: (selectedGames: string[]) => void;
	initialSelected?: string[];
}

const MoreGamesPopupV2: React.FC<MoreGamesPopupV2Props> = ({
	isOpen,
	isPrerendering = false,
	onClose,
	onConfirm,
	initialSelected = [],
}) => {
	const { data } = useViewModel();
	const popupMoreGames = data?.strings.product.configBundles.gamesWidget.popupMoreGames;

	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const updateDebouncedSearch = useDebouncedCallback((value: string) => {
		setDebouncedSearch(value);
	}, 300);
	const [selected, setSelected] = useState<string[]>(initialSelected);
	const [pinned, setPinned] = useState<string[]>([]);
	const [fetchedGames, setFetchedGames] = useState<Record<string, GameThumbnail>>({});

	useEffect(() => {
		if (!isOpen) return;

		setSelected(initialSelected);
		setPinned(initialSelected);

		if (initialSelected.length > 0) {
			getGamesByTitles(initialSelected)
				.then((games) => {
					const updates: Record<string, GameThumbnail> = {};
					games.forEach((g) => { if (g) updates[norm(g.title)] = g; });
					if (Object.keys(updates).length > 0) {
						setFetchedGames((prev) => ({ ...prev, ...updates }));
					}
				});
		}
	}, [isOpen]);

	const { data: popularGames = [] } = usePopularGames(isOpen);
	const { data: allTitles = [] } = useAllGameTitles(isOpen);

	useEffect(() => {
		if (!isOpen) return;

		if (document.querySelector(`link[rel="preconnect"][href="${S3_ORIGIN}"]`)) {
			return;
		}

		const link = document.createElement('link');
		link.rel = 'preconnect';
		link.href = S3_ORIGIN;
		document.head.appendChild(link);

		return () => {
			document.head.removeChild(link);
		};
	}, [isOpen]);


	const isSearching = debouncedSearch.length > 0;

	const searchResults = useMemo(() => {
		if (!isSearching) return [];
		const query = debouncedSearch.toLowerCase();
		return allTitles.filter((g) =>
			g.title.toLowerCase().includes(query)
			|| g.alternativeTitles?.some((alt) => alt.toLowerCase().includes(query))
		);
	}, [debouncedSearch, allTitles, isSearching]);

	const gridGames = useMemo<GameThumbnail[]>(() => {
		const pinnedItems = pinned.map((normalizedTitle) => {
			return popularGames.find((g) => norm(g.title) === normalizedTitle)
				?? fetchedGames[normalizedTitle]
				?? ({ id: normalizedTitle, title: normalizedTitle, coverImage: null, thumbnailImage: null } as GameThumbnail);
		});
		const rest = popularGames.filter((g) => !pinned.includes(norm(g.title)));
		return [...pinnedItems, ...rest].slice(0, MAX_SELECTION);
	}, [pinned, popularGames, fetchedGames]);

	const deselectGame = (normalizedTitle: string) => {
		setSelected((prev) => prev.filter((t) => t !== normalizedTitle));
		setPinned((prev) => prev.filter((t) => t !== normalizedTitle));
	};

	const toggleGridGame = (title: string) => {
		const normalizedTitle = norm(title);
		if (selected.includes(normalizedTitle)) {
			deselectGame(normalizedTitle);
		} else if (selected.length < MAX_SELECTION) {
			setSelected((prev) => [...prev, normalizedTitle]);
		}
	};

	const handleSearchSelect = (title: string) => {
		const normalizedTitle = norm(title);
		if (selected.includes(normalizedTitle)) {
			deselectGame(normalizedTitle);
		} else if (selected.length < MAX_SELECTION) {
			setSelected((prev) => [...prev, normalizedTitle]);
			setPinned((prev) => (prev.includes(normalizedTitle) ? prev : [normalizedTitle, ...prev]));

			const alreadyHave = popularGames.some((g) => norm(g.title) === normalizedTitle) || fetchedGames[normalizedTitle];
			if (!alreadyHave) {
				getGamesByTitles([title])
					.then(([game]) => {
						if (game) setFetchedGames((prev) => ({ ...prev, [normalizedTitle]: game }));
					})
					.catch(() => {});
			}
		}
	};

	const handleConfirm = () => {
		if (selected.length === 0) {
			onClose();
			return;
		}
		onConfirm(selected);
	};

	return (
		<Modal
			isOpen={isOpen}
			isPrerendering={isPrerendering}
			onClose={onClose}
			closeOnOverlay
			size="md"
			ariaLabelledBy="more-games-title"
			ariaDescribedBy="more-games-description"
		>
			<div className={styles.popup}>
				<div className={styles.header}>
					<h4 id="more-games-title" className={styles.popupHeader}>
						{popupMoreGames?.headerTitle}
					</h4>
				</div>

				<div className={styles.controls}>
					<div className={styles.searchWrapper}>
						<input
							className={styles.searchInput}
							type="text"
							placeholder={popupMoreGames?.searchForGamesPlaceholder}
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								updateDebouncedSearch(e.target.value);
							}}
						/>
						<svg
							className={styles.searchIcon}
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							aria-hidden="true"
						>
							<circle cx="11" cy="11" r="8" />
							<path d="M21 21l-4.35-4.35" />
						</svg>
					</div>
				</div>

				{isSearching ? (
					<div
						className={styles.searchList}
						role="group"
						aria-label="Søkeresultater"
					>
						{searchResults.map((game) => {
							const isSelected = selected.includes(norm(game.title));
							const isDisabled =
								!isSelected && selected.length >= MAX_SELECTION;
							return (
								<div
									key={game.id}
									className={clsx(
										styles.searchListItem,
										isSelected && styles.searchListItemSelected,
										isDisabled && styles.searchListItemDisabled,
									)}
									onClick={() => !isDisabled && handleSearchSelect(game.title)}
									role="checkbox"
									aria-checked={isSelected}
									tabIndex={0}
									onKeyDown={(e) => {
										if (e.key === KEY_ENTER || e.key === KEY_SPACE) {
											e.preventDefault();
											if (!isDisabled) handleSearchSelect(game.title);
										}
									}}
								>
									<span className={styles.searchListItemTitle}>
										{game.title}
									</span>
									<div
										className={clsx(
											styles.searchListCheckbox,
											isSelected && styles.checkboxChecked,
										)}
										aria-hidden="true"
									>
										{isSelected && <Check />}
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div className={styles.gridContainer}>
						<h4 className={styles.popupHeader}>
							{popupMoreGames?.popularGamesHeader}
						</h4>
						<div
							className={styles.gameGrid}
							role="group"
							aria-label="Velg spill"
						>
							{gridGames.map((game) => {
								const isSelected = selected.includes(norm(game.title));
								const isDisabled =
									!isSelected && selected.length >= MAX_SELECTION;
								return (
									<div
										key={game.id}
										className={clsx(
											styles.gameCard,
											isSelected && styles.gameCardSelected,
											isDisabled && styles.gameCardDisabled,
										)}
										onClick={() => !isDisabled && toggleGridGame(game.title)}
										role="checkbox"
										aria-checked={isSelected}
										tabIndex={0}
										onKeyDown={(e) => {
											if (e.key === KEY_ENTER || e.key === KEY_SPACE) {
												e.preventDefault();
												if (!isDisabled) toggleGridGame(game.title);
											}
										}}
									>
										{game.thumbnailImage ? (
											<img
												src={game.thumbnailImage.url}
												alt={game.title}
												className={styles.gameCover}
												fetchPriority="high"
												decoding="async"
												width={game.thumbnailImage.width ?? THUMBNAIL_WIDTH}
												height={game.thumbnailImage.height ?? THUMBNAIL_HEIGHT}
											/>
										) : (
											<div
												className={styles.gameCoverPlaceholder}
												aria-hidden="true"
											/>
										)}
										<span className={styles.gameCardTitle}>{game.title}</span>
										<div
											className={clsx(
												styles.checkbox,
												isSelected && styles.checkboxChecked,
											)}
											aria-hidden="true"
										>
											{isSelected && (
												<svg
													width="12"
													height="12"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="3"
												>
													<path d="M20 6L9 17l-5-5" />
												</svg>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}

				<div className={styles.footer}>
					<button className={styles.cancelButton} onClick={onClose}>
						{popupMoreGames?.cancelButton}
					</button>
					<button className={styles.confirmButton} onClick={handleConfirm}>
						{popupMoreGames?.okButton}
					</button>
				</div>
			</div>
		</Modal>
	);
};

export default MoreGamesPopupV2;